import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
   updateDevice,
   renameRuijieSsid,
   listDeviceRatesApi,
   createDeviceRateApi,
   updateDeviceRateApi,
   deleteDeviceRateApi,
   type ApiVendoDevice,
   type VendoRateApi,
   type PortalConfig,
} from '@/api/vendo';
import { clientChangePasswordApi, type ClientProfile } from '@/api/clientPortal';
import PortalAdvancedDrawer from '@/components/Admin/Prompt/PortalAdvancedDrawer';
import DurationUnitInput, {
   decomposeMinutes,
} from '@/components/Admin/UI/DurationUnitInput';
import { coinBoxHoldsRename, coinBoxRescueName } from '@/utils/coinBoxRenameHold';
import SsidRenameProgress from '@/components/Admin/UI/SsidRenameProgress';
import { normalizePortalConfig } from '@/components/Admin/Prompt/portalVibes';
import {
   Section,
   SectionHead,
   SectionTitle,
   Hint,
   FormRow,
   FormField,
   FormLabel,
   FormInput,
   FieldError,
   FormActions,
   SmallBtn,
   GhostBtn,
   DangerSmallBtn,
   ToggleBtn,
} from './shared';

/**
 * Basic portal — Settings tab: rate plans (coin + voucher), bandwidth,
 * portal theme (incl. the full Advanced designer), hardware placeholder,
 * reset sales and password. All server-guarded to this one device.
 */

const PORTAL_PREVIEW_HOST = import.meta.env.DEV
   ? `http://${window.location.hostname}:3002`
   : import.meta.env.VITE_PORTAL_BASE_URL || (window.location.hostname === 'app-staging.smartfi.ph' ? 'https://portal-staging.mysmartfi.ph' : 'https://portal.mysmartfi.ph');

const SettingsPage: React.FC<{
   device: ApiVendoDevice;
   profile: ClientProfile;
   onChanged: () => void;
}> = ({ device, profile, onChanged }) => {
   const isRuijie = device.router?.platform === 'openwrt';
   const canSsid = profile.permissions?.renameSsid !== false;
   return (
      <>
         <RatesSection
            deviceId={device._id}
            dataPlanId={device.activeDataPlan ?? null}
         />
         {isRuijie && canSsid && (
            <SsidSection device={device} onChanged={onChanged} />
         )}
         <BandwidthSection device={device} onChanged={onChanged} />
         <ThemeSection device={device} onChanged={onChanged} />
         <Section>
            <SectionTitle>🔧 Hardware</SectionTitle>
            <Hint>
               Coin-box repair tools (reflash) are coming soon. For hardware
               problems, contact your seller.
            </Hint>
         </Section>
         <PasswordSection />
      </>
   );
};

/* ── WiFi name (Ruijie only, seller-permitted) ───────────────────── */

export const SsidSection: React.FC<{
   device: ApiVendoDevice;
   onChanged: () => void;
}> = ({ device, onChanged }) => {
   const [ssid, setSsid] = useState(device.router?.ssid || '');
   const [err, setErr] = useState('');
   const [busy, setBusy] = useState(false);
   // The instant OUR Save succeeded — the progress banner shows from this at
   // once, without waiting for the refetch to bring the server's stamp back.
   const [savedAt, setSavedAt] = useState<number | null>(null);
   // With the NodeMCU silent a saved rename parks invisibly forever — see
   // coinBoxHoldsRename. The server refuses it too (409 coinbox-offline).
   // Exception: the rescue direction (the name the NodeMCU was flashed for)
   // stays savable, so a name-mismatched box can be fixed from here.
   const coinBoxWaiting = coinBoxHoldsRename(device, ssid);
   const rescueName = coinBoxRescueName(device);

   const save = async () => {
      const trimmed = ssid.trim();
      if (trimmed.length < 2) {
         setErr('Type the WiFi name (at least 2 characters)');
         return;
      }
      setErr('');
      setBusy(true);
      try {
         await renameRuijieSsid(device._id, trimmed);
         toast.success('WiFi name saved — the router applies it shortly');
         setSavedAt(Date.now());
         onChanged();
      } catch (e: any) {
         toast.error(
            e?.response?.data?.msg ||
               e?.response?.data?.error ||
               'Could not save the WiFi name'
         );
      } finally {
         setBusy(false);
      }
   };

   return (
      <Section>
         <SectionTitle>📡 WiFi name (SSID)</SectionTitle>
         <Hint>
            The network name your customers see when they search for WiFi.
            The vendo applies the change within a minute.
         </Hint>
         <FormField style={{ marginTop: 10 }}>
            <FormLabel>WiFi name</FormLabel>
            <FormInput
               value={ssid}
               onChange={(e) => setSsid(e.target.value)}
               maxLength={32}
               placeholder="Superman WiFi"
            />
         </FormField>
         {err && <FieldError>{err}</FieldError>}
         <SsidRenameProgress device={device} savedAt={savedAt} onPoll={onChanged} />
         {coinBoxWaiting && (
            <CoinBoxWaitNote>
               Waiting for the NodeMCU — it is offline right now. The WiFi
               name can only change while the NodeMCU is online, so it can
               learn the new name first and follow the router to it.
               {rescueName && rescueName !== (device.router?.ssid || '') && (
                  <>
                     {' '}One exception: you can still set it to{' '}
                     <b>{rescueName}</b> — the name the NodeMCU was flashed
                     with — to help it find the network again.
                  </>
               )}
            </CoinBoxWaitNote>
         )}
         <FormActions>
            <SmallBtn type="button" onClick={save} disabled={busy || coinBoxWaiting}>
               {busy ? 'Saving…' : 'Save WiFi name'}
            </SmallBtn>
         </FormActions>
      </Section>
   );
};

/* ── Rate plans (coin + voucher) ─────────────────────────────────── */

export const RatesSection: React.FC<{ deviceId: string; dataPlanId: string | null }> = ({
   deviceId,
   dataPlanId,
}) => {
   const queryClient = useQueryClient();
   const { data: rates = [] } = useQuery({
      queryKey: ['client-rates', deviceId],
      queryFn: () => listDeviceRatesApi(deviceId),
   });
   const [rateTab, setRateTab] = useState<'coin' | 'voucher'>('coin');
   const [adding, setAdding] = useState(false);
   const [editing, setEditing] = useState<string | null>(null);
   const [price, setPrice] = useState('');
   // Duration is stored in minutes; DurationUnitInput lets the user type
   // it in minutes/hours/days/months (same control as the operator's
   // rate editor) and converts for us.
   const [durationMins, setDurationMins] = useState<number | null>(null);
   const [label, setLabel] = useState('');
   const [err, setErr] = useState('');
   const [busy, setBusy] = useState(false);
   const [confirmDel, setConfirmDel] = useState<string | null>(null);

   const shown = useMemo(
      () => rates.filter((r) => (r.type ?? 'coin') === rateTab),
      [rates, rateTab]
   );
   const refresh = () =>
      queryClient.invalidateQueries({ queryKey: ['client-rates', deviceId] });

   const startAdd = () => {
      setAdding(true);
      setEditing(null);
      setPrice('');
      setDurationMins(null);
      setLabel('');
      setErr('');
   };
   const startEdit = (r: VendoRateApi) => {
      setEditing(r._id);
      setAdding(false);
      setPrice(String(r.price));
      setDurationMins(r.duration);
      setLabel(r.label);
      setErr('');
   };
   const closeForm = () => {
      setAdding(false);
      setEditing(null);
      setErr('');
   };

   const save = async () => {
      const p = Number(price);
      const m = durationMins ?? 0;
      if (!p || p <= 0) {
         setErr('Type the price in pesos (example: 5)');
         return;
      }
      if (!m || m <= 0) {
         setErr('Type the duration (example: 30 minutes)');
         return;
      }
      setBusy(true);
      try {
         if (editing) {
            await updateDeviceRateApi(editing, {
               price: p,
               duration: m,
               label: label.trim() || `₱${p}`,
            });
         } else {
            await createDeviceRateApi(deviceId, {
               price: p,
               duration: m,
               label: label.trim() || `₱${p}`,
               type: rateTab,
            });
         }
         toast.success('Rates updated');
         closeForm();
         refresh();
      } catch (e: any) {
         setErr(e?.response?.data?.error || 'Could not save — try again');
      } finally {
         setBusy(false);
      }
   };

   const remove = async (rateId: string) => {
      setBusy(true);
      try {
         await deleteDeviceRateApi(rateId);
         setConfirmDel(null);
         toast.success('Rate removed');
         refresh();
      } catch (e: any) {
         toast.error(e?.response?.data?.error || 'Could not remove');
      } finally {
         setBusy(false);
      }
   };

   // "45 minutes" / "1.5 hours" / "1 day" / "1 month" — same decomposition
   // the duration input uses, so the row shows what the operator typed.
   const fmt = (mins: number) => {
      const d = decomposeMinutes(mins);
      const unit = d.value === 1 ? d.unit.slice(0, -1) : d.unit;
      return `${d.value} ${unit}`;
   };

   // Inline row editor — the row being edited (or added) swaps its own
   // content for inputs, so nothing below jumps around (Ray: the old
   // bottom form container was confusing to edit with).
   const editorRow = (key: string) => (
      <RateEditRow key={key}>
         <MiniField>
            <MiniLabel>Price ₱</MiniLabel>
            <MiniInput
               inputMode="numeric"
               value={price}
               onChange={(e) => setPrice(e.target.value)}
               placeholder="5"
               autoFocus
            />
         </MiniField>
         <MiniField $duration>
            <MiniLabel>Duration</MiniLabel>
            <DurationUnitInput value={durationMins} onChange={setDurationMins} />
         </MiniField>
         <MiniField $grow>
            <MiniLabel>Name (optional)</MiniLabel>
            <MiniInput
               value={label}
               onChange={(e) => setLabel(e.target.value)}
               placeholder="Quick browse"
               maxLength={60}
            />
         </MiniField>
         <RowBtns>
            <SmallBtn type="button" onClick={save} disabled={busy}>
               {busy ? 'Saving…' : 'Save'}
            </SmallBtn>
            <GhostBtn type="button" onClick={closeForm}>
               Cancel
            </GhostBtn>
         </RowBtns>
         {err && <RowError>{err}</RowError>}
      </RateEditRow>
   );

   return (
      <Section>
         <SectionHead>
            <SectionTitle>💰 Rate plans</SectionTitle>
            {!adding && !editing && (
               <SmallBtn type="button" onClick={startAdd}>
                  + Add rate
               </SmallBtn>
            )}
         </SectionHead>
         <RateTabs>
            <RateTabBtn
               type="button"
               $active={rateTab === 'coin'}
               onClick={() => {
                  setRateTab('coin');
                  closeForm();
               }}
            >
               Coin
            </RateTabBtn>
            <RateTabBtn
               type="button"
               $active={rateTab === 'voucher'}
               onClick={() => {
                  setRateTab('voucher');
                  closeForm();
               }}
            >
               Voucher
            </RateTabBtn>
         </RateTabs>
         <Hint>
            {rateTab === 'coin'
               ? 'How much time customers get for their coins.'
               : 'Voucher rates customers can buy with GCash or codes you sell.'}
         </Hint>

         {shown.map((r) =>
            editing === r._id ? (
               editorRow(r._id)
            ) : (
               <RateRow key={r._id}>
                  <RatePrice>₱{r.price}</RatePrice>
                  <RateInfo>
                     <RateLabel>{r.label}</RateLabel>
                     <RateTime>{fmt(r.duration)}</RateTime>
                  </RateInfo>
                  {confirmDel === r._id ? (
                     <>
                        <DangerSmallBtn
                           type="button"
                           onClick={() => remove(r._id)}
                           disabled={busy}
                        >
                           Remove
                        </DangerSmallBtn>
                        <GhostBtn type="button" onClick={() => setConfirmDel(null)}>
                           Keep
                        </GhostBtn>
                     </>
                  ) : (
                     <>
                        <GhostBtn type="button" onClick={() => startEdit(r)}>
                           Edit
                        </GhostBtn>
                        <GhostBtn type="button" onClick={() => setConfirmDel(r._id)}>
                           ✕
                        </GhostBtn>
                     </>
                  )}
               </RateRow>
            )
         )}
         {shown.length === 0 && !adding && (
            <Hint>No {rateTab} rates yet — add your first one.</Hint>
         )}
         {adding && editorRow('new-rate')}
         {rateTab === 'voucher' && (
            <BulkPrintRow>
               {/* Printing moved to the Vouchers tab (full table + wizard, Ray 2026-10-18). */}
               <Hint>Generate and print voucher cards from the <strong>Vouchers</strong> tab.</Hint>
            </BulkPrintRow>
         )}
      </Section>
   );
};

/* ── Bandwidth & general ─────────────────────────────────────────── */

const BandwidthSection: React.FC<{
   device: ApiVendoDevice;
   onChanged: () => void;
}> = ({ device, onChanged }) => {
   const [ratePerPeso, setRatePerPeso] = useState(
      String(device.settings?.ratePerPeso ?? 6)
   );
   const [pauseEnabled, setPauseEnabled] = useState(
      !!device.settings?.pauseEnabled
   );
   const [download, setDownload] = useState(
      String(device.settings?.defaultDownload ?? 0)
   );
   const [upload, setUpload] = useState(
      String(device.settings?.defaultUpload ?? 0)
   );
   const [err, setErr] = useState('');
   const [busy, setBusy] = useState(false);

   const save = async () => {
      const rpp = Number(ratePerPeso);
      if (!rpp || rpp <= 0) {
         setErr('Minutes per peso must be a number (example: 6)');
         return;
      }
      setErr('');
      setBusy(true);
      try {
         await updateDevice(device._id, {
            settings: {
               ...device.settings,
               ratePerPeso: rpp,
               pauseEnabled,
               defaultDownload: Number(download) || 0,
               defaultUpload: Number(upload) || 0,
            },
         });
         toast.success('Settings saved');
         onChanged();
      } catch (e: any) {
         toast.error(e?.response?.data?.error || 'Could not save settings');
      } finally {
         setBusy(false);
      }
   };

   return (
      <Section>
         <SectionTitle>📶 Bandwidth & general</SectionTitle>
         <FormRow>
            <FormField>
               <FormLabel>Minutes per ₱1 (coins)</FormLabel>
               <FormInput
                  inputMode="numeric"
                  value={ratePerPeso}
                  onChange={(e) => setRatePerPeso(e.target.value)}
               />
            </FormField>
            <FormField>
               <FormLabel>Customers can pause time</FormLabel>
               <ToggleBtn
                  type="button"
                  $on={pauseEnabled}
                  onClick={() => setPauseEnabled((v) => !v)}
               >
                  {pauseEnabled ? 'Yes' : 'No'}
               </ToggleBtn>
            </FormField>
         </FormRow>
         <FormRow>
            <FormField>
               <FormLabel>Download speed (Mbps, 0 = no limit)</FormLabel>
               <FormInput
                  inputMode="numeric"
                  value={download}
                  onChange={(e) => setDownload(e.target.value)}
               />
            </FormField>
            <FormField>
               <FormLabel>Upload speed (Mbps, 0 = no limit)</FormLabel>
               <FormInput
                  inputMode="numeric"
                  value={upload}
                  onChange={(e) => setUpload(e.target.value)}
               />
            </FormField>
         </FormRow>
         {err && <FieldError>{err}</FieldError>}
         <FormActions>
            <SmallBtn type="button" onClick={save} disabled={busy}>
               {busy ? 'Saving…' : 'Save settings'}
            </SmallBtn>
         </FormActions>
      </Section>
   );
};

/* ── Portal theme ────────────────────────────────────────────────── */

const THEMES: { key: string; name: string; swatch: string }[] = [
   { key: 'gaming', name: 'Gaming', swatch: '#0c0c1d' },
   { key: 'midnight', name: 'Midnight', swatch: '#020617' },
   { key: 'clean', name: 'Clean', swatch: '#f8fafc' },
   { key: 'sunset', name: 'Sunset', swatch: '#4b1c30' },
   { key: 'ocean', name: 'Ocean', swatch: '#042f3a' },
   { key: 'custom', name: 'Customizable', swatch: '#4f7cff' },
];

const ThemeSection: React.FC<{
   device: ApiVendoDevice;
   onChanged: () => void;
}> = ({ device, onChanged }) => {
   const [saving, setSaving] = useState(false);
   const [drawerOpen, setDrawerOpen] = useState(false);
   const [config, setConfig] = useState<PortalConfig>(() =>
      normalizePortalConfig(device.portalConfig ?? null)
   );
   const current = device.portalTheme || 'gaming';

   const pick = async (key: string) => {
      if (key === 'custom') {
         setDrawerOpen(true);
         return;
      }
      setSaving(true);
      try {
         await updateDevice(device._id, { portalTheme: key as any });
         toast.success('Theme saved');
         onChanged();
      } catch (e: any) {
         toast.error(e?.response?.data?.error || 'Could not save the theme');
      } finally {
         setSaving(false);
      }
   };

   const saveCustom = async () => {
      setSaving(true);
      try {
         await updateDevice(device._id, {
            portalTheme: 'custom',
            portalConfig: config,
         });
         toast.success('Portal design saved');
         onChanged();
      } catch (e: any) {
         toast.error(e?.response?.data?.error || 'Could not save the design');
      } finally {
         setSaving(false);
      }
   };

   const openPreview = () => {
      const slug = device.portalSlug || 'demo';
      window.open(
         `${PORTAL_PREVIEW_HOST}/${encodeURIComponent(slug)}?preview=1`,
         '_blank',
         'noopener,noreferrer'
      );
   };

   return (
      <Section>
         <SectionTitle>🎨 Portal theme</SectionTitle>
         <Hint>The look your customers see when they connect.</Hint>
         <ThemeGrid>
            {THEMES.map((t) => (
               <ThemeCard
                  key={t.key}
                  type="button"
                  $active={current === t.key}
                  onClick={() => pick(t.key)}
                  disabled={saving}
               >
                  <ThemeSwatch style={{ background: t.swatch }} />
                  {t.name}
               </ThemeCard>
            ))}
         </ThemeGrid>
         {current === 'custom' && (
            <FormActions>
               <SmallBtn type="button" onClick={() => setDrawerOpen(true)}>
                  Customize design
               </SmallBtn>
               <GhostBtn type="button" onClick={openPreview}>
                  Preview ↗
               </GhostBtn>
            </FormActions>
         )}
         <PortalAdvancedDrawer
            open={drawerOpen}
            value={config}
            onChange={setConfig}
            onClose={() => setDrawerOpen(false)}
            onSave={saveCustom}
            onPreview={openPreview}
            saving={saving}
         />
      </Section>
   );
};

/* ── Change password ─────────────────────────────────────────────── */

const PasswordSection: React.FC = () => {
   const [open, setOpen] = useState(false);
   const [current, setCurrent] = useState('');
   const [next, setNext] = useState('');
   const [err, setErr] = useState('');
   const [busy, setBusy] = useState(false);

   const save = async () => {
      if (!current) {
         setErr('Type your current password');
         return;
      }
      if (next.length < 6) {
         setErr('New password must be at least 6 characters');
         return;
      }
      setErr('');
      setBusy(true);
      try {
         await clientChangePasswordApi({
            currentPassword: current,
            newPassword: next,
         });
         toast.success('Password changed');
         setOpen(false);
         setCurrent('');
         setNext('');
      } catch (e: any) {
         setErr(e?.response?.data?.error || 'Could not change the password');
      } finally {
         setBusy(false);
      }
   };

   return (
      <Section>
         <SectionTitle>🔒 Password</SectionTitle>
         <Hint>
            Note: the seller of your vendo can also see this password, so
            they can help you if you forget it.
         </Hint>
         {!open ? (
            <FormActions>
               <GhostBtn type="button" onClick={() => setOpen(true)}>
                  Change my password
               </GhostBtn>
            </FormActions>
         ) : (
            <>
               <FormRow>
                  <FormField>
                     <FormLabel>Current password</FormLabel>
                     <FormInput
                        type="password"
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                     />
                  </FormField>
                  <FormField>
                     <FormLabel>New password</FormLabel>
                     <FormInput
                        type="password"
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                     />
                  </FormField>
               </FormRow>
               {err && <FieldError>{err}</FieldError>}
               <FormActions>
                  <SmallBtn type="button" onClick={save} disabled={busy}>
                     {busy ? 'Saving…' : 'Save password'}
                  </SmallBtn>
                  <GhostBtn type="button" onClick={() => setOpen(false)}>
                     Cancel
                  </GhostBtn>
               </FormActions>
            </>
         )}
      </Section>
   );
};

export default SettingsPage;

const CoinBoxWaitNote = styled.div`
   font-size: 12px;
   line-height: 1.5;
   color: #8a6d1a;
   background: #fdf6e3;
   border: 1px solid #f0e0b0;
   border-radius: 8px;
   padding: 8px 12px;
   margin-top: 8px;
`;

const BulkPrintRow = styled.div`
   display: flex;
   justify-content: flex-end;
   margin-top: 12px;
`;

const RateTabs = styled.div`
   margin-top: 10px;
   display: flex;
   gap: 6px;
`;

const RateTabBtn = styled.button<{ $active: boolean }>`
   border: 1.5px solid
      ${({ $active }) => ($active ? '#4f7cff' : 'var(--app-border, #e3e8ef)')};
   background: ${({ $active }) => ($active ? '#4f7cff14' : 'transparent')};
   color: ${({ $active }) => ($active ? '#4f7cff' : 'var(--app-text-dim, #55606c)')};
   border-radius: 999px;
   padding: 6px 16px;
   font-size: 12.5px;
   font-weight: 700;
   cursor: pointer;
`;

const RateRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   margin-top: 10px;
   border: 1px solid var(--app-border, #e3e8ef);
   border-radius: 10px;
   padding: 10px 12px;
`;

const RatePrice = styled.div`
   font-size: 17px;
   font-weight: 800;
   min-width: 46px;
`;

const RateInfo = styled.div`
   flex: 1;
   min-width: 0;
`;

const RateLabel = styled.div`
   font-size: 13px;
   font-weight: 700;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;

const RateTime = styled.div`
   font-size: 11.5px;
   color: var(--app-text-muted, #7b8794);
`;

// Inline editor variant of RateRow — same card look, inputs in the row.
const RateEditRow = styled.div`
   display: flex;
   align-items: flex-end;
   gap: 8px;
   flex-wrap: wrap;
   margin-top: 10px;
   border: 1px solid var(--app-accent, #4f7cff);
   border-radius: 10px;
   padding: 10px 12px;
`;

const MiniField = styled.div<{ $grow?: boolean; $duration?: boolean }>`
   display: flex;
   flex-direction: column;
   gap: 3px;
   width: ${({ $grow, $duration }) =>
      $grow ? 'auto' : $duration ? '170px' : '76px'};
   ${({ $grow }) => ($grow ? 'flex: 1; min-width: 120px;' : '')}
`;

const MiniLabel = styled.label`
   font-size: 10.5px;
   font-weight: 700;
   color: var(--app-text-muted, #7b8794);
`;

const MiniInput = styled.input`
   width: 100%;
   border: 1px solid var(--app-border, #e3e8ef);
   border-radius: 8px;
   padding: 7px 9px;
   font-size: 13.5px;
   font-weight: 600;
   background: var(--app-surface, #fff);
   color: inherit;
   &:focus {
      outline: none;
      border-color: var(--app-accent, #4f7cff);
   }
`;

const RowBtns = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
`;

const RowError = styled.div`
   flex-basis: 100%;
   font-size: 11.5px;
   font-weight: 600;
   color: #d64545;
`;

const ThemeGrid = styled.div`
   margin-top: 10px;
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 8px;
`;

const ThemeCard = styled.button<{ $active: boolean }>`
   border: 2px solid
      ${({ $active }) => ($active ? '#4f7cff' : 'var(--app-border, #e3e8ef)')};
   background: transparent;
   border-radius: 10px;
   padding: 8px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   display: flex;
   flex-direction: column;
   gap: 6px;
   align-items: center;
   &:disabled {
      opacity: 0.6;
   }
`;

const ThemeSwatch = styled.div`
   width: 100%;
   height: 34px;
   border-radius: 7px;
   border: 1px solid var(--app-border, #e3e8ef);
`;
